import api from './api';

export type SolutionType = 'NAC' | '유해사이트' | 'DLP';

export interface ExceptionItem {
  id: number;
  solution: SolutionType;
  exceptionItem?: string;
  exceptionDetail?: string;
  company?: string;
  department?: string;
  name?: string;
  reason?: string;
  approvalDocNo?: string;
  applyDate?: string;
  startDate?: string;
  endDate?: string;
  actionType?: string;
  exceptionContent?: string;
  remark?: string;
  createdAt?: string;
}

export interface NacExceptionRequest {
  id?: number;
  exceptionItem?: string;
  company?: string;
  department?: string;
  name?: string;
  ip?: string;
  reason?: string;
  approvalDocNo?: string;
  applyDate?: string;
  startDate?: string;
  endDate?: string;
  actionType?: string;
  actor?: string;
  applyGroup?: string;
  exceptionContent?: string;
  remark?: string;
}

export interface SiteExceptionRequest {
  id?: number;
  exceptionItem?: string;
  exceptionDetail?: string;
  company?: string;
  department?: string;
  name?: string;
  reason?: string;
  approvalDocNo?: string;
  applyDate?: string;
  startDate?: string;
  endDate?: string;
  actionType?: string;
  exceptionContent?: string;
  remark?: string;
}

export interface DlpExceptionRequest {
  id?: number;
  exceptGroup?: string;
  exceptDetail?: string;
  exceptDetailId?: number;
  reqCompany?: string;
  reqDepartment?: string;
  reqName?: string;
  targetCompany?: string;
  permittedIp?: string;
  permittedPc?: string;
  reason?: string;
  period?: string;
  approval?: string;
  actor?: string;
  applyYn?: string;
  applyDateDisplay?: string;
  applyDate?: string;
  endDate?: string;
  applyMethod?: string;
  remark?: string;
}

export interface DlpEnumItem {
  id: number;
  itemName: string;
  itemCode: number;
  itemEnum: string;
}

export interface GetAllExceptionsResult {
  items: ExceptionItem[];
  total: number;
}

export interface ExceptionCompany {
  orgCode: string;
  companyName: string;
  nacUseYn?: string;
  dlpUseYn?: string;
  webFilterUseYn?: string;
}

// NAC 예외항목 ('NAC 1일 인증 예외' → 'NAC 로그인 예외' 변경)
export const NAC_EXCEPTION_ITEMS = [
  'NAC 설치 예외',
  'NAC 로그인 예외',
  'NAC 일주일 1회 인증',
  'NAC 한달 1회 인증',
] as const;

export const SITE_EXCEPTION_ITEMS = ['웹사이트 예외', '웹사이트 차단', '기타'] as const;

export const SITE_EXCEPTION_DETAILS = [
  '웹메일', '가상화폐/채굴_마이닝', '게임', '경영_기업', '커뮤니티_동호회',
  '컴퓨터_인터넷_IT', '문화_예술', '생성형 인공지능', '쇼핑', '소셜미디어',
  '스트리밍미디어', '스포츠', '엔터테인먼트', '메일 대용량 다운로드', '웹오피스',
  '웹하드', '웹툰_만화', '인터넷방송_UCC', '인터넷일반', '임시웹페이지',
  '전단지_리다이렉션', '증권', '채용', '투자정보', '프록시',
  '모든 정책 비활성화', '기타', '모바일 서비스',
] as const;

// DLP 예외항목 → 예외상세 매핑 (티켓 본문 '선택 3: 세아홀딩스 DLP' 표 기준)
export const DLP_EXCEPTION_MAP: Record<string, readonly string[]> = {
  '공유폴더예외': ['공유폴더 차단정책 비활성화', '기타', '미사용 라이선스 삭제', '모든 정책 비활성화'],
  '소프트웨어예외': ['메신저 (소프트웨어)', 'FTP', '메일 클라이언트', '클라우드 스토리지', '원격제어', '웹브라우저', '미분류 (소프트웨어)', '소프트웨어 차단정책 비활성화'],
  '워터마크예외': ['모든문서 출력 차단', '개인정보 문서 출력 차단', '워터마크 해제', '워터마크 자동 승인'],
  '웹사이트예외': ['웹메일', '쇼핑', '증권', '게임', '엔터테인먼트', '뉴스', 'SNS', '웹하드/클라우드스토리지', '보안위협', '성인/유해', '미분류 (웹사이트)', '유해사이트 차단(전사)', '웹사이트 차단정책 비활성화'],
  '이동식디스크예외': ['매체 예외 등록', '매체 차단정책 비활성화', '미사용 매체 삭제'],
  '파일첨부차단예외': ['메신저 (파일첨부)', '확장자 설정', '파일첨부 차단정책 비활성화'],
  '기타': ['미사용 라이선스', '모든 정책 비활성화'],
};

export const DLP_EXCEPTION_ITEMS = Object.keys(DLP_EXCEPTION_MAP) as string[];

// 솔루션 → is_company_info 컬럼 매핑 (새 솔루션 추가 시 여기에만 추가)
export const SOLUTION_USEYN_KEY: Record<SolutionType, keyof ExceptionCompany> = {
  NAC: 'nacUseYn',
  DLP: 'dlpUseYn',
  '유해사이트': 'webFilterUseYn',
};

export const securityExceptionService = {
  getAll: async (params?: {
    solution?: SolutionType;
    company?: string;
    exceptionItem?: string;
    exceptionDetail?: string;
    name?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortKey?: string;
    sortDir?: 'asc' | 'desc';
  }): Promise<GetAllExceptionsResult> => {
    const query = new URLSearchParams();
    if (params?.solution) query.set('solution', params.solution);
    if (params?.company) query.set('company', params.company);
    if (params?.exceptionItem) query.set('exceptionItem', params.exceptionItem);
    if (params?.exceptionDetail) query.set('exceptionDetail', params.exceptionDetail);
    if (params?.name) query.set('name', params.name);
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.sortKey) query.set('sortKey', params.sortKey);
    if (params?.sortDir) query.set('sortDir', params.sortDir);
    const qs = query.toString();
    const res = await api.get<GetAllExceptionsResult>(`/security-exception${qs ? `?${qs}` : ''}`);
    return res.data;
  },

  getCompanies: async (): Promise<ExceptionCompany[]> => {
    const res = await api.get<ExceptionCompany[]>('/company/for-exception');
    return res.data;
  },

  // NAC
  createNac: async (dto: NacExceptionRequest) => {
    const res = await api.post('/security-exception/nac', dto);
    return res.data;
  },
  updateNac: async (id: number, dto: NacExceptionRequest) => {
    const res = await api.patch(`/security-exception/nac/${id}`, dto);
    return res.data;
  },
  deleteNac: async (id: number) => {
    await api.delete(`/security-exception/nac/${id}`);
  },

  // 유해사이트
  createSite: async (dto: SiteExceptionRequest) => {
    const res = await api.post('/security-exception/site', dto);
    return res.data;
  },
  updateSite: async (id: number, dto: SiteExceptionRequest) => {
    const res = await api.patch(`/security-exception/site/${id}`, dto);
    return res.data;
  },
  deleteSite: async (id: number) => {
    await api.delete(`/security-exception/site/${id}`);
  },

  // DLP
  getDlpEnumList: async (): Promise<DlpEnumItem[]> => {
    const res = await api.get<DlpEnumItem[]>('/security-exception/dlp/enum-list');
    return res.data;
  },
  createDlp: async (dto: DlpExceptionRequest) => {
    const res = await api.post('/security-exception/dlp', dto);
    return res.data;
  },
  updateDlp: async (id: number, dto: DlpExceptionRequest) => {
    const res = await api.patch(`/security-exception/dlp/${id}`, dto);
    return res.data;
  },
  deleteDlp: async (id: number) => {
    await api.delete(`/security-exception/dlp/${id}`);
  },

  // 벌크 업로드
  bulkCreate: async (solution: SolutionType, rows: Record<string, string>[]) => {
    const res = await api.post('/security-exception/bulk', { solution, rows });
    return res.data as { count: number };
  },

  nacExcelUpload: async (file: File): Promise<{ inserted: number }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post('/security-exception/nac/excel-upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
};
